/**
 * @owner @Appboy/frontend-ix
 */
import { QueryClient } from "@tanstack/react-query";
import type { DefaultOptions, NetworkMode } from "@tanstack/react-query";

// react-query's default "online" mode pauses every query while navigator.onLine reads
// false, with no request and no error. That flag can stay stuck false while traffic flows.
//
// Read through a getter rather than at module scope: packs assign
// window.brazeDashboardConfig after their import graph has evaluated, so a snapshot taken
// here is empty for any entry point that does not defer its imports. react-query resolves
// default options per query, so the getter runs once the config is present.
const readNetworkMode = (): NetworkMode =>
  window.brazeDashboardConfig?.features?.react_query_network_mode_always ? "always" : "online";

/**
 * The query defaults every dashboard `QueryClient` shares. Pass the object itself, never a
 * spread of it: spreading evaluates `networkMode` once, at module scope.
 */
export const queryDefaultOptions: NonNullable<DefaultOptions["queries"]> = {
  get networkMode(): NetworkMode {
    return readNetworkMode();
  },
  refetchInterval: false,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: false,
  // Pinned: react-query defaults this to `networkMode !== "always"`, which would strand a query that errored offline.
  refetchOnReconnect: true,
};

/**
 * This is the cache controller for react-query. It can be used to flush or prime the cache in any environment
 *
 * It is also used in the top-level provider for the react binding defined in app/javascript/src/lib/reactApp.tsx
 *
 * @example
 * import { queryClient } from "@lib/queryClient";
 * queryClient.invalidateCache("canvasDigest");
 *
 * Mutations are left on react-query's default, so a save paused by a real outage resumes on
 * reconnect rather than failing.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: queryDefaultOptions,
  },
});

export * from "./runOperatorQuery";
